/* eslint-disable react-refresh/only-export-components -- router config module:
   the lazy() route consts are not Fast-Refresh component exports, and this file
   intentionally only exports the `router` instance. */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { env } from '@/env';
import { AppShell } from '@/components/layout/AppShell';
import { Loader } from '@/components/common/Loader';
import { AuthCallback } from './AuthCallback';
import { Hub } from './Hub';
import { Tournaments } from './Tournaments';
import { TournamentDetail } from './TournamentDetail';
import { WatchParty } from './WatchParty';
import { MatchDetail } from './MatchDetail';
import { TeamDetail } from './TeamDetail';
import { ChatList } from './ChatList';
import { ChatRoom } from './ChatRoom';
import { Profile } from './Profile';
import { UserProfile } from './UserProfile';
import { Leaderboards } from './Leaderboards';
import { LeaderboardDetail } from './LeaderboardDetail';
import { News } from './News';
import { ArticleDetail } from './ArticleDetail';
import { NewsTag } from './NewsTag';
import { NewsPreferences } from './NewsPreferences';
import { Settings } from './Settings';
import { Friends } from './Friends';
import { DuelLink } from './duels/DuelLink';

// DuelResult is lazy: it pulls the QBot card catalog + saved-deck + social
// surfaces, only reached after a completed duel — keep it out of the main bundle.
const DuelResult = lazy(() => import('./duels/DuelResult').then((m) => ({ default: m.DuelResult })));

// ── Code-split route trees ────────────────────────────────────────────────
// Avatar, Riot and Clash Royale screens are lazy so their component code
// lands in dedicated chunks (see vite.config.ts manualChunks). Combined with
// the game-conditional Hub, a Clash Royale player never navigates toward the
// Riot tree and so never downloads its chunk. The RTK Query slices themselves
// (qbotApi/qriotsApi/avatarApi) stay in the main bundle for now because the
// store imports them eagerly — disaccoppiamento is a follow-up.

// Avatars
const Avatars = lazy(() => import('./Avatars').then((m) => ({ default: m.Avatars })));
const AvatarCreate = lazy(() => import('./AvatarCreate').then((m) => ({ default: m.AvatarCreate })));
const AvatarDetail = lazy(() => import('./AvatarDetail').then((m) => ({ default: m.AvatarDetail })));
const AvatarEdit = lazy(() => import('./AvatarEdit').then((m) => ({ default: m.AvatarEdit })));
const AvatarSeries = lazy(() => import('./AvatarSeries').then((m) => ({ default: m.AvatarSeries })));
const AvatarClipDetail = lazy(() =>
  import('./AvatarClipDetail').then((m) => ({ default: m.AvatarClipDetail })),
);
const AvatarEditor = lazy(() =>
  import('./avatar-editor/AvatarEditor').then((m) => ({ default: m.AvatarEditor })),
);
const AvatarAssistant = lazy(() =>
  import('./avatar-assistant/AvatarAssistant').then((m) => ({ default: m.AvatarAssistant })),
);

// Riot
const Riot = lazy(() => import('./Riot').then((m) => ({ default: m.Riot })));
const RiotLolDetail = lazy(() => import('./RiotLolDetail').then((m) => ({ default: m.RiotLolDetail })));
const RiotLolMastery = lazy(() =>
  import('./RiotLolMastery').then((m) => ({ default: m.RiotLolMastery })),
);
const RiotLolMatches = lazy(() =>
  import('./RiotLolMatches').then((m) => ({ default: m.RiotLolMatches })),
);
const RiotLolStats = lazy(() => import('./RiotLolStats').then((m) => ({ default: m.RiotLolStats })));
const RiotMultiSearch = lazy(() =>
  import('./RiotMultiSearch').then((m) => ({ default: m.RiotMultiSearch })),
);
const RiotSaved = lazy(() => import('./RiotSaved').then((m) => ({ default: m.RiotSaved })));
const RiotTftDetail = lazy(() => import('./RiotTftDetail').then((m) => ({ default: m.RiotTftDetail })));

// Clash Royale
const CRDecks = lazy(() => import('./cr/CRDecks').then((m) => ({ default: m.CRDecks })));
const CRLink = lazy(() => import('./cr/CRLink').then((m) => ({ default: m.CRLink })));
const CRSavedDecks = lazy(() => import('./cr/CRSavedDecks').then((m) => ({ default: m.CRSavedDecks })));
const CRPopularDecks = lazy(() =>
  import('./cr/CRPopularDecks').then((m) => ({ default: m.CRPopularDecks })),
);
const CRBattles = lazy(() => import('./cr/CRBattles').then((m) => ({ default: m.CRBattles })));
const CRBattleDetail = lazy(() =>
  import('./cr/CRBattleDetail').then((m) => ({ default: m.CRBattleDetail })),
);
const CRPlayerProfile = lazy(() =>
  import('./cr/CRPlayerProfile').then((m) => ({ default: m.CRPlayerProfile })),
);
const CRDeckCheck = lazy(() => import('./cr/CRDeckCheck').then((m) => ({ default: m.CRDeckCheck })));
const CRDeckBuilder = lazy(() =>
  import('./cr/CRDeckBuilder').then((m) => ({ default: m.CRDeckBuilder })),
);

// QChess — chess tournaments + duels on Lichess + QLASH leaderboard.
const Chess = lazy(() => import('./Chess').then((m) => ({ default: m.Chess })));
const ChessTournaments = lazy(() =>
  import('./ChessTournaments').then((m) => ({ default: m.ChessTournaments })),
);
const ChessTournamentDetail = lazy(() =>
  import('./ChessTournamentDetail').then((m) => ({ default: m.ChessTournamentDetail })),
);
const ChessChallenges = lazy(() =>
  import('./ChessChallenges').then((m) => ({ default: m.ChessChallenges })),
);
const ChessChallengeDetail = lazy(() =>
  import('./ChessChallengeDetail').then((m) => ({ default: m.ChessChallengeDetail })),
);
const ChessLeaderboard = lazy(() =>
  import('./ChessLeaderboard').then((m) => ({ default: m.ChessLeaderboard })),
);
const ChessBrackets = lazy(() =>
  import('./ChessBrackets').then((m) => ({ default: m.ChessBrackets })),
);
const ChessBracketDetail = lazy(() =>
  import('./ChessBracketDetail').then((m) => ({ default: m.ChessBracketDetail })),
);

// QClans — Clash of Clans stats / war tracking / competitions / AI coaching.
const CocHome = lazy(() => import('./coc/CocHome').then((m) => ({ default: m.CocHome })));
const CocLink = lazy(() => import('./coc/CocLink').then((m) => ({ default: m.CocLink })));
const CocProfile = lazy(() => import('./coc/CocProfile').then((m) => ({ default: m.CocProfile })));
const CocClan = lazy(() => import('./coc/CocClan').then((m) => ({ default: m.CocClan })));
const CocCompetitions = lazy(() =>
  import('./coc/CocCompetitions').then((m) => ({ default: m.CocCompetitions })),
);
const CocCompetitionDetail = lazy(() =>
  import('./coc/CocCompetitionDetail').then((m) => ({ default: m.CocCompetitionDetail })),
);
const CocCoach = lazy(() => import('./coc/CocCoach').then((m) => ({ default: m.CocCoach })));

/** Wrap a lazy route element in a Suspense boundary with the app Loader. */
function lazyRoute(node: ReactNode): ReactNode {
  return <Suspense fallback={<Loader />}>{node}</Suspense>;
}

// Routes gated by env feature flags. When the corresponding feature is
// disabled (PROD), the route entry is omitted from the children array — a
// direct URL like `/avatars`, `/riot`, or `/cr` falls through to the `*`
// catch-all and redirects to `/`. Filtering at module load is fine: env
// values are frozen at build time, so the router never needs to re-evaluate.
const avatarRoutes: RouteObject[] = env.featureAvatarsEnabled
  ? [
      { path: 'avatars', element: lazyRoute(<Avatars />) },
      { path: 'avatars/new', element: lazyRoute(<AvatarCreate />) },
      { path: 'avatars/new/assistant', element: lazyRoute(<AvatarAssistant />) },
      { path: 'avatars/:id', element: lazyRoute(<AvatarDetail />) },
      { path: 'avatars/:id/edit', element: lazyRoute(<AvatarEdit />) },
      { path: 'avatars/:id/series', element: lazyRoute(<AvatarSeries />) },
      { path: 'avatars/:id/clips/:clipId', element: lazyRoute(<AvatarClipDetail />) },
    ]
  : [];

const riotRoutes: RouteObject[] = env.featureQriotsEnabled
  ? [
      { path: 'riot', element: lazyRoute(<Riot />) },
      { path: 'riot/saved', element: lazyRoute(<RiotSaved />) },
      { path: 'riot/multi-search', element: lazyRoute(<RiotMultiSearch />) },
      { path: 'riot/lol/:puuid', element: lazyRoute(<RiotLolDetail />) },
      { path: 'riot/lol/:puuid/matches', element: lazyRoute(<RiotLolMatches />) },
      { path: 'riot/lol/:puuid/stats', element: lazyRoute(<RiotLolStats />) },
      { path: 'riot/lol/:puuid/mastery', element: lazyRoute(<RiotLolMastery />) },
      { path: 'riot/tft/:puuid', element: lazyRoute(<RiotTftDetail />) },
    ]
  : [];

const crRoutes: RouteObject[] = env.featureClashRoyaleEnabled
  ? [
      // /cr is now a redirect to the Mazzi landing (compat with old links /
      // the CRHubTile). The deck-focused landing is /cr/decks.
      { path: 'cr', element: <Navigate to="/cr/decks" replace /> },
      { path: 'cr/decks', element: lazyRoute(<CRDecks />) },
      { path: 'cr/link', element: lazyRoute(<CRLink />) },
      { path: 'cr/decks/saved', element: lazyRoute(<CRSavedDecks />) },
      { path: 'cr/decks/popular', element: lazyRoute(<CRPopularDecks />) },
      { path: 'cr/decks/check', element: lazyRoute(<CRDeckCheck />) },
      { path: 'cr/decks/builder', element: lazyRoute(<CRDeckBuilder />) },
      { path: 'cr/battles', element: lazyRoute(<CRBattles />) },
      { path: 'cr/battles/:idx', element: lazyRoute(<CRBattleDetail />) },
      { path: 'cr/player/:tag', element: lazyRoute(<CRPlayerProfile />) },
    ]
  : [];

const chessRoutes: RouteObject[] = env.featureChessEnabled
  ? [
      { path: 'chess', element: lazyRoute(<Chess />) },
      { path: 'chess/tournaments', element: lazyRoute(<ChessTournaments />) },
      { path: 'chess/tournaments/:id', element: lazyRoute(<ChessTournamentDetail />) },
      { path: 'chess/brackets', element: lazyRoute(<ChessBrackets />) },
      { path: 'chess/brackets/:id', element: lazyRoute(<ChessBracketDetail />) },
      { path: 'chess/challenges', element: lazyRoute(<ChessChallenges />) },
      { path: 'chess/challenges/:id', element: lazyRoute(<ChessChallengeDetail />) },
      { path: 'chess/leaderboard', element: lazyRoute(<ChessLeaderboard />) },
    ]
  : [];

const cocRoutes: RouteObject[] = env.featureCocEnabled
  ? [
      { path: 'coc', element: lazyRoute(<CocHome />) },
      { path: 'coc/link', element: lazyRoute(<CocLink />) },
      { path: 'coc/profile', element: lazyRoute(<CocProfile />) },
      { path: 'coc/clan', element: lazyRoute(<CocClan />) },
      { path: 'coc/competitions', element: lazyRoute(<CocCompetitions />) },
      { path: 'coc/competitions/:id', element: lazyRoute(<CocCompetitionDetail />) },
      { path: 'coc/coach', element: lazyRoute(<CocCoach />) },
    ]
  : [];

export const router = createBrowserRouter([
  // Public route: must NOT be wrapped in AppShell (no auth gating, no TabBar)
  // — AppShell would loop the user back to <Login /> before the OIDC code is
  // exchanged for tokens.
  { path: '/auth/callback', element: <AuthCallback /> },

  // Avatar Editor wizard runs full-screen — bypass AppShell so the TabBar
  // doesn't intercept clicks on the sticky footer Next/Back buttons.
  ...(env.featureAvatarsEnabled
    ? [{ path: '/avatars/editor', element: lazyRoute(<AvatarEditor />) }]
    : []),

  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Hub /> },

      { path: 'tournaments', element: <Tournaments /> },
      { path: 'tournaments/:id', element: <TournamentDetail /> },
      { path: 'tournaments/:id/watch-party', element: <WatchParty /> },

      { path: 'matches/:id', element: <MatchDetail /> },
      { path: 'teams/:id', element: <TeamDetail /> },

      { path: 'chat', element: <ChatList /> },
      { path: 'chat/:id', element: <ChatRoom /> },

      { path: 'profile', element: <Profile /> },
      { path: 'profile/:id', element: <UserProfile /> },

      { path: 'friends', element: <Friends /> },

      // Clash Royale account linking for 1v1 duels (qduels verify-token).
      // Prerequisite for the friends-list "Sfida" button — reached from
      // Profile → Linked accounts and from the gated Sfida affordance.
      { path: 'duels/link', element: <DuelLink /> },

      // Post-match duel result — outcome + both decks + opponent social/deck
      // actions. Reached from the QueueSheet (queue completion) and the Sfide
      // tab (tapping a completed challenge). Optional `?challengeId=` adds the
      // series X–Y header.
      { path: 'duels/result/:gameTag', element: lazyRoute(<DuelResult />) },

      { path: 'leaderboards', element: <Leaderboards /> },
      { path: 'leaderboards/:id', element: <LeaderboardDetail /> },

      { path: 'news', element: <News /> },
      { path: 'news/preferences', element: <NewsPreferences /> },
      { path: 'news/tag/:slug', element: <NewsTag /> },
      { path: 'news/:slug', element: <ArticleDetail /> },

      ...avatarRoutes,
      ...riotRoutes,
      ...crRoutes,
      ...chessRoutes,
      ...cocRoutes,

      { path: 'settings', element: <Settings /> },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
